import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LeadSections } from "@/components/LeadSections";
import { CaptureButtons } from "@/components/CaptureButtons";
import { FormHeader } from "@/components/FormHeader";
import { ConsentChecks, PRIVACY_ERROR } from "@/components/ConsentChecks";
import { EMPTY_LEAD } from "@/lib/leadConstants";
import { API } from "@/lib/api";

export const FieraFields = ({ form, set, prefix = "" }) => (
  <div className="grid grid-cols-3 gap-3">
    <div className="col-span-2 space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Nome Fiera</Label>
      <Input
        data-testid={`${prefix}input-nome-fiera`}
        value={form.nome_fiera}
        onChange={(e) => set("nome_fiera", e.target.value)}
        placeholder="Es. Cibus 2026"
        className="min-h-[56px] rounded-xl text-base"
      />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Anno</Label>
      <Input
        data-testid={`${prefix}input-anno`}
        type="number"
        value={form.anno}
        onChange={(e) => set("anno", parseInt(e.target.value) || 2026)}
        className="min-h-[56px] rounded-xl text-base"
      />
    </div>
  </div>
);

export default function LeadForm() {
  const [form, setForm] = useState(EMPTY_LEAD);
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const toggle = (key, val) =>
    setForm((f) => {
      const arr = f[key];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  const inArr = (key, val) => form[key].includes(val);

  const handleCapture = (data, photo) =>
    setForm((f) => ({ ...f, ...data, ...(photo ? { card_photo: photo } : {}) }));

  const handleSave = async () => {
    if (!form.ragione_sociale && !form.nome_buyer) {
      toast.error("Inserisci almeno Ragione Sociale o Nome Buyer");
      return;
    }
    if (!form.consenso_privacy) return toast.error(PRIVACY_ERROR);
    setSaving(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Scheda salvata con successo");
      setForm({ ...EMPTY_LEAD, nome_fiera: form.nome_fiera, anno: form.anno });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-body" data-testid="lead-form-page">
      <FormHeader historyPath="/storico" historyLabel="Elenco Contatti Raccolti" />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-6 pb-32 space-y-6">
        <div className="text-center py-2">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-buscema-green">Scheda Contatto Buyer</h1>
        </div>
        <FieraFields form={form} set={set} />
        <CaptureButtons onData={handleCapture} photo={form.card_photo} />
        <LeadSections form={form} set={set} toggle={toggle} inArr={inArr} />
        <ConsentChecks form={form} set={set} />
      </main>
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 p-4">
        <div className="max-w-3xl mx-auto">
          <Button
            data-testid="save-lead-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full min-h-[60px] rounded-xl bg-buscema-green hover:bg-buscema-greenDark text-white text-lg font-semibold gap-3 shadow-lg"
          >
            {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Save className="w-6 h-6" />}
            Salva Scheda
          </Button>
        </div>
      </div>
    </div>
  );
}
